// src/pages/EnquenteSanteDemographe.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Divider,
    FormControl,
    Select,
    MenuItem,
    Tabs,
    Tab,
} from '@mui/material';
import Footer from '../navbar/Footer';
import { getPublications } from '../services/publicationService';

function EnquenteSanteDemographe() {
    const [activeTab, setActiveTab] = useState(0);

    // États pour les données
    const [demographyData, setDemographyData] = useState([]);
    const [healthData, setHealthData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // États pour les filtres et la pagination
    const [demographyYear, setDemographyYear] = useState('');
    const [healthYear, setHealthYear] = useState('');
    const [showMoreDemography, setShowMoreDemography] = useState(false);
    const [showMoreHealth, setShowMoreHealth] = useState(false);

    // Chargement des données depuis l'API
    useEffect(() => {
        setLoading(true);
        Promise.all([
            getPublications({ type: 'red', perPage: 1000 }),   // démographie
            getPublications({ type: 'res', perPage: 1000 })    // santé
        ])
            .then(([resDem, resHealth]) => {
                if (resDem.error) setError(resDem.error);
                else if (resHealth.error) setError(resHealth.error);
                else {
                    // Mapper les données démographie
                    const mappedDem = resDem.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        description: item.description,
                        date: new Date(item.date),
                        image: item.image_url || 'asset/cover/cover.jpg', // fallback
                    }));
                    // Mapper les données santé
                    const mappedHealth = resHealth.data.map(item => ({
                        title: item.title,
                        link: item.document_url,
                        description: item.description,
                        date: new Date(item.date),
                        image: item.image_url || 'asset/cover/cover.jpg',
                    }));
                    // Trier par date décroissante (plus récent en premier)
                    mappedDem.sort((a, b) => b.date - a.date);
                    mappedHealth.sort((a, b) => b.date - a.date);
                    setDemographyData(mappedDem);
                    setHealthData(mappedHealth);
                }
            })
            .catch(err => {
                setError('Erreur lors du chargement des données.');
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, []);

    // Années disponibles pour chaque onglet
    const demographyYears = useMemo(() => {
        const years = demographyData.map(item => item.date.getFullYear());
        return [...new Set(years)].sort((a, b) => b - a);
    }, [demographyData]);

    const healthYears = useMemo(() => {
        const years = healthData.map(item => item.date.getFullYear());
        return [...new Set(years)].sort((a, b) => b - a);
    }, [healthData]);

    // Données filtrées
    const filteredDemography = useMemo(() => {
        if (!demographyYear) return demographyData;
        return demographyData.filter(item => item.date.getFullYear().toString() === demographyYear);
    }, [demographyData, demographyYear]);

    const filteredHealth = useMemo(() => {
        if (!healthYear) return healthData;
        return healthData.filter(item => item.date.getFullYear().toString() === healthYear);
    }, [healthData, healthYear]);

    // Gestionnaires
    const handleChangeTab = (event, newValue) => {
        setActiveTab(newValue);
        // Réinitialiser les affichages lors du changement d'onglet
        setShowMoreDemography(false);
        setShowMoreHealth(false);
        setDemographyYear('');
        setHealthYear('');
    };

    const handleShowMoreDemography = () => setShowMoreDemography(!showMoreDemography);
    const handleShowMoreHealth = () => setShowMoreHealth(!showMoreHealth);

    // Limite d'affichage par défaut (2 rapports)
    const visibleDemography = showMoreDemography ? filteredDemography : filteredDemography.slice(0, 2);
    const visibleHealth = showMoreHealth ? filteredHealth : filteredHealth.slice(0, 2);

    if (loading) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                    <Typography variant="body1">Chargement des données...</Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
                <NavigationBar />
                <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                    <Typography variant="body1" color="error">{error}</Typography>
                </Container>
                <Footer />
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: '#f5f5f5', paddingBottom: '20px' }}>
            <NavigationBar />
            <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleChangeTab}
                    textColor="inherit"
                    indicatorColor="secondary"
                >
                    <Tab label="Démographie" />
                    <Tab label="Santé" />
                </Tabs>

                {/* Section Démographie */}
                {activeTab === 0 && (
                    <>
                        <Divider style={{ margin: '20px 0' }} />
                        <Typography variant="h4" align="justify" gutterBottom style={{ color: '#2E7D32' }}>
                            Rapports des enquêtes: Démographie
                        </Typography>
                        <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '20px' }}>
                            Cette section présente des rapports sur les tendances démographiques et la santé, fournissant des données utiles pour les politiques publiques et les recherches.
                        </Typography>

                        <FormControl variant="outlined" style={{ marginLeft: '0px', width: '300px', marginBottom: '20px' }}>
                            <Select
                                value={demographyYear}
                                onChange={(e) => setDemographyYear(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Toutes les années</em>
                                </MenuItem>
                                {demographyYears.map(year => (
                                    <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Grid container spacing={1}>
                            {visibleDemography.map((document, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card elevation={3} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Grid container>
                                            <Grid item xs={3}>
                                                <a href={document.link} style={{ textDecoration: 'none', color: 'inherit' }} target="_blank" rel="noopener noreferrer">
                                                    <img src={document.image} alt="Cover" style={{ width: '100%', height: 'auto' }} />
                                                </a>
                                            </Grid>
                                            <Grid item xs={9}>
                                                <CardContent>
                                                    <Typography variant="body1" component="div" style={{ fontWeight: 'bold', color: '#2E7D32' }}>
                                                        <a href={document.link} style={{ textDecoration: 'none', color: 'inherit' }} target="_blank" rel="noopener noreferrer">
                                                            <DownloadIcon /> {document.title}
                                                        </a>
                                                    </Typography>
                                                    <Typography variant="body2">{document.description}</Typography>
                                                </CardContent>
                                            </Grid>
                                        </Grid>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <Button
                            onClick={handleShowMoreDemography}
                            style={{
                                marginTop: '20px',
                                marginBottom: '20px',
                                backgroundColor: 'green',
                                color: 'white',
                            }}
                        >
                            {showMoreDemography ? 'Moins de rapports' : 'Plus de rapports'}
                        </Button>
                    </>
                )}

                {/* Section Santé */}
                {activeTab === 1 && (
                    <>
                        <Divider style={{ margin: '20px 0' }} />
                        <Typography variant="h4" align="justify" gutterBottom style={{ color: '#2E7D32' }}>
                            Rapports des enquêtes: Santé
                        </Typography>
                        <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '20px' }}>
                            Découvrez les rapports récents sur la santé, qui évaluent l'impact de divers programmes et initiatives de santé publique au Burundi.
                        </Typography>

                        <FormControl variant="outlined" style={{ marginLeft: '0px', width: '300px', marginBottom: '20px' }}>
                            <Select
                                value={healthYear}
                                onChange={(e) => setHealthYear(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Toutes les années</em>
                                </MenuItem>
                                {healthYears.map(year => (
                                    <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Grid container spacing={1}>
                            {visibleHealth.map((annuaire, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card elevation={3} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Grid container>
                                            <Grid item xs={4}>
                                                <a href={annuaire.link} style={{ textDecoration: 'none', color: 'inherit' }} target="_blank" rel="noopener noreferrer">
                                                    <img src={annuaire.image} alt="Cover" style={{ width: '100%', height: 'auto' }} />
                                                </a>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <CardContent>
                                                    <Typography variant="body1" component="div" style={{ fontWeight: 'bold', color: '#2E7D32' }}>
                                                        <a href={annuaire.link} style={{ textDecoration: 'none', color: 'inherit' }} target="_blank" rel="noopener noreferrer">
                                                            <DownloadIcon /> {annuaire.title}
                                                        </a>
                                                    </Typography>
                                                    <Typography variant="body2">{annuaire.description}</Typography>
                                                </CardContent>
                                            </Grid>
                                        </Grid>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <Button
                            onClick={handleShowMoreHealth}
                            style={{
                                marginTop: '20px',
                                marginBottom: '20px',
                                backgroundColor: 'green',
                                color: 'white',
                            }}
                        >
                            {showMoreHealth ? 'Voir moins de rapports' : 'Plus de rapports'}
                        </Button>
                    </>
                )}
            </Container>
            <Footer />
        </div>
    );
}

export default EnquenteSanteDemographe;